/**
 * Question Renderer Component
 * Reusable component for rendering questions using Nagwa Questions Engine
 */

import { useEffect, useRef, useState } from "react";
import { globalConfig } from "../../config/globalConfig";
import { useResources } from "../../hooks/useResources";
import { getQuestionPresignedUrls } from "../../services/question.service";
import type {
  QuestionRendererProps,
  QuestionEngineMessage,
  QuestionEngineInitFromPackagePayload,
  QuestionMetadata,
} from "../../interfaces/componentInterfaces/questionRenderer";
import "./QuestionRenderer.scss";

const QuestionRenderer = ({
  questionId,
  jobId,
  packageBasePath: providedPackageBasePath,
  onRenderComplete,
  onRenderError,
  onMetadataLoaded,
  className = "",
}: QuestionRendererProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const isMountedRef = useRef(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { getResourceValue } = useResources();
  
  // Store callbacks in refs to avoid dependency changes
  const onRenderCompleteRef = useRef(onRenderComplete);
  const onRenderErrorRef = useRef(onRenderError);
  const onMetadataLoadedRef = useRef(onMetadataLoaded);
  
  useEffect(() => {
    onRenderCompleteRef.current = onRenderComplete;
    onRenderErrorRef.current = onRenderError;
    onMetadataLoadedRef.current = onMetadataLoaded;
  }, [onRenderComplete, onRenderError, onMetadataLoaded]);

  useEffect(() => {
    isMountedRef.current = true;
    
    // Reset loading state for new question
    setIsLoading(true);
    setError(null);
    
    // Clean up any existing iframe before rendering new question
    if (iframeRef.current && containerRef.current) {
      try {
        containerRef.current.removeChild(iframeRef.current);
      } catch (_) {
        // Ignore if already removed
      }
      iframeRef.current = null;
    }

    const handleMessage = (event: MessageEvent) => {
      if (!containerRef.current) return;

      try {
        const data: QuestionEngineMessage = JSON.parse(event.data);
        const messageKey = data.messageKey;

        // Handle question rendered and height changed events
        if (["questionRendered", "questionHeightChanged"].includes(messageKey)) {
          if (data.questionId === questionId && data.questionHeight) {
            const newHeight = data.questionHeight;
            if (containerRef.current) {
              containerRef.current.style.height = `${newHeight}px`;
            }
          }

          // Call success callback on first render and extract metadata from callback
          if (messageKey === "questionRendered" && data.questionId === questionId) {
            setIsLoading(false);
            onRenderCompleteRef.current?.();

            // Extract metadata from the questionRendered callback
            const metadata: QuestionMetadata = {
              parentId: String(data.parentId ?? ""),
              country: String(data.country ?? ""),
              subject: String(data.subject ?? ""),
              grade: String(data.grade ?? ""),
              sectionId: String(data.sectionId ?? ""),
              language: String(data.language ?? ""),
              questionTypes: String(data.questionTypes ?? ""),
            };
            onMetadataLoadedRef.current?.(metadata);
          }
        }
      } catch (_) {
        // Ignore parse errors from other messages
      }
    };

    window.addEventListener("message", handleMessage);

    const renderQuestion = async () => {
      if (!containerRef.current || !isMountedRef.current) return;

      // Check if iframe already exists to prevent duplicate rendering
      if (iframeRef.current) {
        return;
      }

      try {
        const engineURL = globalConfig.questionsEngineUrl;
        
        // If packageBasePath is provided, use it directly; otherwise fetch from API
        let presignedUrlPromise: Promise<string> | null = null;
        if (!providedPackageBasePath) {
          // Construct the S3 object key for the package based on whether we have a jobId
          const objectKey = jobId
            ? `jobs/${jobId}/questions/${questionId}/package/${questionId}.zip`
            : `packages/${questionId}.zip`;

          // Fetch presigned URL for the question package (parallel with iframe creation)
          presignedUrlPromise = getQuestionPresignedUrls([objectKey]).then(response => {
            if (!response || response.length === 0) {
              throw new Error("Question not found: No presigned URL returned");
            }
            return response[0].presigned_url;
          });
        }

        // Create iframe
        const iframe = document.createElement("iframe");
        iframeRef.current = iframe;

        iframe.onload = async () => {
          if (!isMountedRef.current) return;

          try {
            // Use provided packageBasePath or wait for the API response
            const resolvedPackageBasePath = providedPackageBasePath ?? await presignedUrlPromise!;

            const payload: QuestionEngineInitFromPackagePayload = {
              action: "initFromPackage",
              payload: {
                packageBasePath: resolvedPackageBasePath,
                enginesBasePath: globalConfig.enginesBasePath,
                questionFilePath: `${questionId}.json`,
                mode: "homework_worksheet_preview",
              },
            }; 

            iframe.contentWindow?.postMessage(JSON.stringify(payload), "*");
          } catch (err) {
            console.error("Error loading question:", err);
            if (isMountedRef.current) {
              // Check if the error is a 403/404 (question not found)
              const isNotFoundError = (err as Error).message?.includes("Question not found");
              const errorMessage = isNotFoundError
                ? getResourceValue(
                    "question_not_found",
                    "Question not found"
                  )
                : getResourceValue(
                    "failed_load_question",
                    "Failed to load question. Please try again."
                  );
              setError(errorMessage);
              setIsLoading(false);
              onRenderErrorRef.current?.(err as Error);
            }
          }
        };

        iframe.onerror = () => {
          if (!isMountedRef.current) return;
          const errorMessage = getResourceValue(
            "failed_load_engine",
            "Failed to load question engine."
          );
          setError(errorMessage);
          setIsLoading(false);
          onRenderErrorRef.current?.(new Error("Failed to load question engine"));
        };

        iframe.src = engineURL;
        iframe.style.width = "100%";
        iframe.style.border = "none";

        if (containerRef.current) {
          containerRef.current.appendChild(iframe);
        }
      } catch (err) {
        console.error("Error rendering question:", err);
        if (isMountedRef.current) {
          const errorMessage = getResourceValue(
            "failed_render_question",
            "Failed to render question. Please try again."
          );
          setError(errorMessage);
          setIsLoading(false);
          onRenderErrorRef.current?.(err as Error);
        }
      }
    };

    renderQuestion();

    // Cleanup function
    return () => {
      isMountedRef.current = false;
      window.removeEventListener("message", handleMessage);
      
      // Clean up iframe when component unmounts or questionId/jobId changes
      if (iframeRef.current && containerRef.current) {
        try {
          containerRef.current.removeChild(iframeRef.current);
        } catch (_) {
          // Ignore if already removed
        }
        iframeRef.current = null;
      }
    };
  }, [questionId, jobId, providedPackageBasePath, getResourceValue]);

  return (
    <div
      ref={containerRef}
      className={`question-renderer ${className}`}
      data-question-id={questionId}
    >
      {isLoading && (
        <div className="question-renderer__loading">
          <div className="question-renderer__spinner"></div>
          <p>{getResourceValue("loading_question", "Loading question...")}</p>
        </div>
      )}
      {error && (
        <div className="question-renderer__error">
          <p>{error}</p>
        </div>
      )}
    </div>
  );
};

export default QuestionRenderer;
